/**
 * Chapter progress — **derived, never stored**.
 *
 * A chapter's percentage is the share of its cached topics that sit in
 * `topics_done`, computed at read time. It used to be a `progress` field written
 * onto the chapter row and recomputed after every completion and every topic sync;
 * with completion in its own store there is nothing to keep in step — the number
 * can't go stale, adding or pruning topics moves it by itself, and restoring a
 * backup of `topics_done` is instantly reflected without a recompute pass.
 *
 * The cost of deriving it is deliberately kept to almost nothing: the denominator
 * comes from the `chapterId` **index keys** (`getAllKeysBy`), so counting a
 * chapter's topics never deserializes their cached thumbnails, and the numerator
 * comes from one read of the id-shaped `topics_done` rows shared across all
 * chapters.
 *
 * A chapter whose topics aren't cached yet has no denominator and stays at 0% —
 * which is also what an announced (`coming_soon`) chapter gets, having nothing
 * cached at all.
 */

import { STORES, getAllKeysBy } from '../db/idb.js';
import { getDoneTopicIds } from './completion.js';

/** One chapter's percentage, given the set of completed ids. */
async function percentFor(chapterId, doneIds) {
  const topicIds = await getAllKeysBy(STORES.topicsHead, 'chapterId', chapterId);
  if (!topicIds.length) return 0;
  const done = topicIds.filter((id) => doneIds.has(id)).length;
  return Math.round((done / topicIds.length) * 100);
}

/**
 * Progress for many chapters at once, as `chapterId → percent` (0–100).
 *
 * Takes the whole list because that is how the home screen asks: one read of the
 * completed ids for all of them, then one index-key count per chapter.
 *
 * @param {Array<string>} chapterIds Chapter ids, in any order.
 * @returns {Promise<Map<string, number>>}
 */
export async function chaptersProgress(chapterIds) {
  const doneIds = await getDoneTopicIds();
  const entries = await Promise.all(
    chapterIds.map(async (chapterId) => [chapterId, await percentFor(chapterId, doneIds)])
  );
  return new Map(entries);
}
